import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { Briefcase, AlertTriangle, CheckCircle } from 'lucide-react';

export const Rule14Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule14 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule14', { ...data, [field]: value });
  };
  
  return (
    <WorkbookPage pageId="rule-14" prevPage="rule-13" nextPage="section-5-divider">
      <RuleHeader ruleNumber="14" title="Entrepreneurship Basics" section="healing" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        If you run a side hustle, freelance, or have a small business (or dream of starting one), 
        you need to know the basics of keeping business and personal money separate, understanding 
        taxes, and paying yourself properly. Mixing these up is one of the fastest ways to create 
        financial chaos.
      </Paragraph>
      
      <Callout type="info">
        <strong>This is a basics overview.</strong> If you have a serious business, work with 
        an accountant or tax professional. This section gives you the foundation.
      </Callout>
      
      <SectionTitle>The #1 Rule: Separate Your Money</SectionTitle>
      
      <div className="my-6 bg-primary/5 border border-primary/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <Briefcase className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold mb-2">Business vs. Personal Money</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Even if your "business" is just a side hustle, keep the money separate from day one.
            </p>
            <div className="grid gap-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-success mt-0.5 shrink-0" />
                <span className="text-sm">Open a separate bank account for business income/expenses</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-success mt-0.5 shrink-0" />
                <span className="text-sm">Only pay yourself from business to personal (don't dip in randomly)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-success mt-0.5 shrink-0" />
                <span className="text-sm">Track all business income and expenses</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-success mt-0.5 shrink-0" />
                <span className="text-sm">Save receipts for business purchases</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <SectionTitle>Understanding Self-Employment Taxes</SectionTitle>
      
      <div className="my-6 bg-warning/5 border border-warning/20 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold mb-2">The Tax Surprise</h4>
            <p className="text-sm text-muted-foreground mb-3">
              When you're self-employed, no one withholds taxes from your income. You owe:
            </p>
            <ul className="text-sm space-y-1">
              <li>• <strong>Self-employment tax:</strong> ~15.3% (Social Security + Medicare)</li>
              <li>• <strong>Income tax:</strong> Based on your tax bracket (10-37%)</li>
              <li>• <strong>State taxes:</strong> Varies by state</li>
            </ul>
            <p className="text-sm mt-3 font-medium">
              Rule of thumb: Set aside 25-30% of every dollar earned for taxes.
            </p>
          </div>
        </div>
      </div>
      
      <ExampleBox title="Quarterly Estimated Taxes">
        <p>If you expect to owe $1,000+ in taxes for the year, you may need to pay 
        quarterly estimated taxes (due April 15, June 15, Sept 15, Jan 15).</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Missing these can result in penalties. Set aside money each month so you're ready.
        </p>
      </ExampleBox>
      
      <SectionTitle>Paying Yourself</SectionTitle>
      <Paragraph>
        Your business income is not your personal income—at least not directly. Here's how to 
        pay yourself properly:
      </Paragraph>
      
      <div className="grid gap-4 my-6">
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">1. Calculate profit</h4>
          <p className="text-sm text-muted-foreground">
            Business Income − Business Expenses = Profit
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">2. Set aside taxes first</h4>
          <p className="text-sm text-muted-foreground">
            Take 25-30% of profit and put it in a separate "taxes" account.
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">3. Reinvest in business if needed</h4>
          <p className="text-sm text-muted-foreground">
            Some profit may need to stay in the business for growth/expenses.
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">4. Pay yourself the rest</h4>
          <p className="text-sm text-muted-foreground">
            Transfer a regular amount to your personal account—like a paycheck.
          </p>
        </div>
      </div>
      
      <ExampleBox title="Example: Side Hustle Payout">
        <p><strong>Monthly business income:</strong> $1,500</p>
        <p><strong>Business expenses:</strong> $200</p>
        <p><strong>Profit:</strong> $1,300</p>
        <div className="mt-3 space-y-1 text-sm">
          <p>Tax set-aside (30%): $390 → Tax account</p>
          <p>Business savings (10%): $130 → Business account</p>
          <p>Pay yourself: $780 → Personal account</p>
        </div>
      </ExampleBox>
      
      <StepList steps={[
        "Open a separate business bank account (many are free).",
        "Track every business dollar in and out (use a simple spreadsheet or app).",
        "Set aside 25-30% of profit for taxes immediately.",
        "Pay yourself regularly—don't just grab money when you need it.",
        "Save receipts for all business expenses (they're tax deductible).",
        "Learn about quarterly estimated taxes if you owe $1,000+/year.",
        "Consider working with a tax professional as your business grows."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Entrepreneurship Worksheet</h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="My business or side hustle (describe):"
            placeholder="What do you do/sell?"
            value={data.businessIdea || ''}
            onChange={(v) => updateData('businessIdea', v)}
            multiline
          />
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={data.separateAccounts || false}
              onChange={(e) => updateData('separateAccounts', e.target.checked)}
              className="w-5 h-5 rounded"
            />
            <span className="text-sm">I have (or will open) a separate business bank account</span>
          </div>
          
          <WorksheetInput
            label="Average monthly business income:"
            placeholder="$___"
            value={data.monthlyIncome || ''}
            onChange={(v) => updateData('monthlyIncome', v)}
          />
          
          <WorksheetInput
            label="Average monthly business expenses:"
            placeholder="$___"
            value={data.monthlyExpenses || ''}
            onChange={(v) => updateData('monthlyExpenses', v)}
          />
          
          <WorksheetInput
            label="My tax set-aside plan:"
            placeholder="How will you save for taxes? (30% in separate account, etc.)"
            value={data.taxPlan || ''}
            onChange={(v) => updateData('taxPlan', v)}
            multiline
          />
          
          <WorksheetInput
            label="How much will I pay myself each month?"
            placeholder="$___"
            value={data.payYourself || ''}
            onChange={(v) => updateData('payYourself', v)}
          />
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I understand the importance of separating business and personal money
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I know to set aside 25-30% for taxes
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I have a plan for paying myself regularly
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Separate business and personal money from day one",
        "Set aside 25-30% of profit for taxes",
        "Pay yourself like an employee—regularly",
        "Track all income and expenses",
        "Save receipts for tax deductions"
      ]} />
      
      <AffirmationBox>
        I run my business with clarity and intention. I honor both my work and my personal needs.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="What dreams or concerns do you have about your business?"
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
